<!--
  ChatDeadlineHead (Spec 130 E6/E11/E16, Spec 133 E8) — the assessment of a skill, derived by the code at read time:
  headline with the badge "Grenzfall" and the offer "Als Wiedervorlage", the fixed blocks with their source link,
  Beweissicherung and "Nicht betrachtet" (collapsed), the assumptions line (with the link "Datum ändern", Spec 141 E6b) and, in the edge case, the referral list.
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { CalendarClock } from 'lucide-svelte';
  import type { DeadlineHead } from '../api/chat';
  import { Badge, Button } from './ui/index';

  export let messageId: string;
  export let head: DeadlineHead;
  export let busy = false;
  /** Spec 141 E6b: only the latest assessment, while no question follows, offers "Datum ändern". */
  export let canChangeDate = false;

  const dispatch = createEventDispatcher<{ followup: { deadlineId: string }; changedate: void }>();
  const LINK_CLASS = 'text-accent underline hover:text-accent-hover [overflow-wrap:anywhere]';

  $: edge = head.severity === 'edge';
</script>

<div
  data-testid="chat-deadline-head-{messageId}"
  class="flex flex-col gap-2 w-full max-w-[85%] px-3 py-2 border rounded-lg {edge
    ? 'bg-warning/10 border-warning/40'
    : 'bg-accent/10 border-accent/40'}"
>
  <div class="flex flex-wrap items-start justify-between gap-2">
    <div class="flex flex-col gap-1 min-w-0">
      {#if edge}
        <span data-testid="chat-deadline-edge-badge" class="self-start">
          <Badge variant="warning">{$_('chat.deadlineEdge')}</Badge>
        </span>
      {/if}
      <p class="text-sm font-semibold text-text break-words">{head.headline}</p>
    </div>
    {#if head.followupDeadlineId}
      {@const deadlineId = head.followupDeadlineId}
      <Button
        variant="secondary"
        size="sm"
        disabled={busy}
        data-testid="chat-followup-button"
        on:click={() => dispatch('followup', { deadlineId })}
      >
        <CalendarClock class="w-4 h-4" strokeWidth={1.5} />
        {$_('chat.followupButton')}
      </Button>
    {/if}
  </div>

  {#if head.blocks.length > 0}
    <ul class="pl-4 space-y-1 text-sm list-disc text-text">
      {#each head.blocks as block (block.text)}
        <li class="break-words">
          {block.text}
          {#if block.sourceUrl}
            <a href={block.sourceUrl} target="_blank" rel="noopener noreferrer" class={LINK_CLASS}
              >{block.sourceRef}</a
            >
          {/if}
        </li>
      {/each}
    </ul>
  {/if}

  {#if head.evidenceTips.length > 0}
    <details class="text-sm text-text">
      <summary class="cursor-pointer font-medium">{$_('chat.evidenceHeading')}</summary>
      <ul class="pl-4 mt-1 space-y-1 list-disc">
        {#each head.evidenceTips as tip (tip)}
          <li class="break-words">{tip}</li>
        {/each}
      </ul>
    </details>
  {/if}

  {#if head.assumptions}
    <p class="text-xs text-text-secondary break-words">
      {head.assumptions}
      {#if canChangeDate}
        <button
          type="button"
          class="{LINK_CLASS} py-1 px-0 bg-transparent border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={busy}
          data-testid="chat-change-date-button"
          on:click={() => dispatch('changedate')}>{$_('chat.changeDateButton')}</button
        >
      {/if}
    </p>
  {/if}

  {#if head.notConsidered.length > 0}
    <details class="text-sm text-text">
      <summary class="cursor-pointer font-medium">{$_('chat.notConsideredHeading')}</summary>
      <ul class="pl-4 mt-1 space-y-1 list-disc">
        {#each head.notConsidered as item (item)}
          <li class="break-words">{item}</li>
        {/each}
      </ul>
    </details>
  {/if}

  {#if head.referrals.length > 0}
    <div class="text-sm text-text">
      <p class="font-medium">{$_('chat.referralsHeading')}</p>
      <ul class="pl-4 mt-1 space-y-1 list-disc">
        {#each head.referrals as referral (referral.name)}
          <li class="break-words">
            {#if referral.url}
              <a href={referral.url} target="_blank" rel="noopener noreferrer" class={LINK_CLASS}
                >{referral.name}</a
              >
            {:else}
              {referral.name}
            {/if}
          </li>
        {/each}
      </ul>
    </div>
  {/if}
</div>
